import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useJob, useDeleteJob, useUpdateJobStatus } from '@/lib/api/hooks/job';
import { ActionDropdown, Button, DeleteModal, PublishModal, UnpublishModal, type DropdownAction } from '@/components/ui';
import { showToast } from '@/lib/toast';
import DOMPurify from 'dompurify';
import { MapPin, Calendar, Briefcase, Mail, PenLine, Check, Info, FileText } from 'lucide-react';

const JobDetailPage: React.FC = () => {
    const { id: jobId } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [statusModalOpen, setStatusModalOpen] = useState(false);

    const { data: jobData, isLoading, error } = useJob(jobId || '');
    const job = jobData?.data;

    const { mutate: deleteJob, isPending: isDeleting } = useDeleteJob();
    const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdateJobStatus();

    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const sanitizedDescription = useMemo(() => {
        if (!job?.description) return '';
        return DOMPurify.sanitize(job.description);
    }, [job?.description]);

    const calculateDaysRemaining = (endDate: string) => {
        try {
            const end = new Date(endDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const diff = Math.floor((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
            return diff;
        } catch {
            return 0;
        }
    };

    if (!jobId) {
        return (
            <div className="text-center p-8 bg-white rounded-lg">
                <h2 className="text-xl font-bold">Job ID missing</h2>
                <button onClick={() => navigate('/admin/jobs')} className="mt-4 text-[#681628] hover:underline">
                    &larr; Back to Jobs
                </button>
            </div>
        );
    }

    const handleEdit = () => {
        navigate(`/admin/jobs/edit/${jobId}`);
    };

    const handleDeleteClick = () => {
        setDeleteModalOpen(true);
    };

    const handleStatusUpdateClick = () => {
        if (!job) return;
        setStatusModalOpen(true);
    };

    const confirmDelete = () => {
        deleteJob(jobId, {
            onSuccess: (response) => {
                if (response.success) {
                    showToast.success('Job deleted successfully!');
                    setDeleteModalOpen(false);
                    navigate('/admin/jobs');
                }
            },
            onError: (error: Error) => {
                showToast.error('Failed to delete: ' + (error.message || 'Unknown error'));
            }
        });
    };

    const confirmStatusUpdate = () => {
        if (!job) return;
        const newStatus = job.status === 'PUBLISH' ? 'DRAFT' : 'PUBLISH';

        updateStatus(
            { jobId, data: { status: newStatus } },
            {
                onSuccess: (response) => {
                    if (response.success) {
                        showToast.success(
                            newStatus === 'PUBLISH'
                                ? 'Job published successfully!'
                                : 'Job unpublished successfully!'
                        );
                        setStatusModalOpen(false);
                    }
                },
                onError: (error: Error) => {
                    showToast.error('Failed to update status: ' + (error.message || 'Unknown error'));
                }
            }
        );
    };

    const daysRemaining = job ? calculateDaysRemaining(job.endDate) : 0;

    const getDropdownActions = (): DropdownAction[] => [
        {
            label: 'Edit',
            onClick: (e) => {
                e.stopPropagation();
                handleEdit();
            }
        },
        {
            label: job?.status === 'PUBLISH' ? 'Unpublish' : 'Publish',
            onClick: (e) => {
                e.stopPropagation();
                handleStatusUpdateClick();
            },
            disabled: isUpdatingStatus
        },
        {
            label: 'Delete',
            onClick: (e) => {
                e.stopPropagation();
                handleDeleteClick();
            },
            variant: 'danger' as const,
            disabled: isDeleting
        }
    ];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#681628] mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading job...</p>
                </div>
            </div>
        );
    }

    if (error || !job) {
        return (
            <div className="text-center p-8 bg-white rounded-lg">
                <h2 className="text-xl font-bold text-red-600">Error loading job</h2>
                <p className="mt-2 text-gray-600">{error?.message || 'Job not found'}</p>
                <button onClick={() => navigate('/admin/jobs')} className="mt-4 text-[#681628] hover:underline">
                    &larr; Back to Jobs
                </button>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-6">

                {/* Breadcrumb */}
                  <div className="flex items-center gap-2 text-sm">
                                    <Link to="/admin/jobs" className="text-forestGreen-100 hover:underline font-medium">
                                        Jobs
                                    </Link>
                                    <span className="text-gray-400">▸</span>
                                    <span className="text-gray-600">{job.jobTitle}</span>
                                </div>

                {/* Header with Title and Actions */}
                <div className="flex items-start justify-between gap-6">
                    <div className="flex-1">
                        <div className="flex items-center gap-4 mb-2">
                            <div className="w-12 h-12 rounded-lg bg-pink-100 flex items-center justify-center">
                                <Briefcase className="w-6 h-6 text-wine-100" />
                            </div>
                            <div className='flex items-center gap-8'>
                                <h1 className="text-xl font-bold text-gray-900">{job.jobTitle}</h1>
                                <span className={`inline-block px-3 py-1 rounded-md text-sm font-normal ${job.status === 'PUBLISH'
                                    ? 'bg-green-50 text-green-700'
                                    : 'bg-gray-100 text-gray-700'
                                }`}>
                                    {job.status === 'PUBLISH' ? '● Published' : '● Draft'}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button
                        variant='secondary'
                            onClick={handleEdit}
                            className="px-4 py-2 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 font-medium"
                        >
                            <PenLine className="w-5 h-5 mr-2" /> Edit Job
                        </Button>
                      
                        <ActionDropdown
                            id={`job-${jobId}`}
                            actions={getDropdownActions()}
                        />
                    </div>
                </div>

                {/* Main Content with Sidebar */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content Area */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Quick Info Bar */}
                        {(() => {
                            const infoItems = [
                                {
                                    icon: <Briefcase className="w-5 h-5 text-green-600" />,
                                    label: 'Job Type',
                                    value: job.jobType?.replace(/_/g, ' ') || 'N/A',
                                },
                                {
                                    icon: <MapPin className="w-5 h-5 text-blue-500" />,
                                    label: 'Location',
                                    value: job.jobLocation || 'N/A',
                                },
                                {
                                    icon: <Calendar className="w-5 h-5 text-blue-500" />,
                                    label: 'Duration',
                                    value: `${formatDate(job.startDate)} – ${formatDate(job.endDate)}`,
                                },
                                {
                                    icon: <Mail className="w-5 h-5 text-red-500" />,
                                    label: 'Submission Email',
                                    value: job.submissionEmail || 'N/A',
                                },
                            ];
                            return (
                                <div className="bg-white border border-gray-200 rounded-xl p-6">
                                    <div className="grid grid-cols-2 gap-4  sm:grid-cols-2 2xl:flex  lg:gap-8">
                                        {infoItems.map((item, i) => (
                                            <div key={i} className={`flex items-center gap-3`}>
                                                <div className="shrink-0 w-9 h-9 rounded-md bg-gray-50 flex items-center justify-center">
                                                    {item.icon}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-sm">{item.label}</p>
                                                    <p className=" font-semibold text-gray-900 ">{item.value}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Description */}
                        <div className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col gap-4">
                                <div className='flex items-center gap-2'>
                                    <div className='rounded-md h-10 w-10 flex items-center justify-center bg-[#E8F6EE]'>
                                <FileText className="w-5 h-5 text-[#0F8A47]" />

                                    </div>
 <h2 className="text-lg font-semibold text-gray-900 ">
                               Description
                            </h2>
                                </div>
                           
                            <div className="prose prose-sm max-w-none text-gray-700"
                                dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
                            />
                        </div>

                  
                    </div>

                    {/* Sidebar */}
                    <div className="lg:col-span-1 space-y-4">
                        {/* Job Status */}
                        <div className="bg-white border border-gray-200 rounded-xl p-6">
                            <h3 className="font-semibold text-gray-900 mb-4">Job Status</h3>
                            <div className="grid grid-cols-[auto_1fr] items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                                    <Check className={`w-6 h-6 ${job.status === 'PUBLISH' ? 'text-green-600' : 'text-gray-400'}`} />
                                </div>
                                <div className='flex flex-col gap-1'>
                                    <p className="font-semibold text-gray-900">{job.status === 'PUBLISH' ? 'Published' : 'Draft'}</p>
                                    <p className="text-sm text-gray-600">
                                        {job.status === 'PUBLISH'
                                            ? 'This job is visible on the website and accepting applications.'
                                            : 'This job is in draft mode and not visible.'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={handleStatusUpdateClick}
                                className={`w-full py-2 px-4 rounded-lg font-medium transition ${job.status === 'PUBLISH'
                                    ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                                    : 'bg-green-50 text-green-600 hover:bg-green-100 border border-green-200'
                                }`}
                            >
                                {job.status === 'PUBLISH' ? 'Unpublish Job' : 'Publish Job'}
                            </button>
                        </div>

                        {/* Application Deadline */}
                        <div className="bg-white border border-gray-200 rounded-xl p-6 grid grid-cols-[auto_1fr] items-center gap-4">
                            <div className='rounded-full h-12 w-12 flex items-center justify-center bg-[#951B2E10]'>
                                 <Calendar className="w-6 h-6 text-wine-100"  /> 
                            </div>
                            <div className='flex flex-col gap-1'>
 <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                               Application Deadline
                            </h3>
                            <p className="font-semibold text-gray-900 text-lg">{formatDate(job.endDate)}</p>
                            <p className="text-sm text-gray-600">11:59 PM (WAT)</p>
                            <p className={`text-sm font-medium ${daysRemaining <= 0 ? 'text-red-600' : daysRemaining <= 30 ? 'text-orange-600' : 'text-green-600'}`}>
                                {daysRemaining <= 0 ? 'Expired' : `${daysRemaining} days remaining`}
                            </p>
                            </div>
                           
                        </div>

            

                        {/* Publishing Information */}
                        <div className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col gap-4">
                            <div className='flex items-center gap-2 '>
                                <div className='rounded-md h-10 w-10 flex items-center justify-center bg-[#9333ea10]'>
                                  <Info className="w-5 h-5 text-[#9333ea]" />

                                </div>
                                   <h3 className="font-semibold text-gray-900">
                               Publishing Information
                            </h3>
                            </div>
                           
                            <div className="space-y-3 text-sm">
                                <div>
                                    <p className="text-gray-500">Published Date</p>
                                    <p className="font-medium text-gray-900">{job.datePublished ? formatDate(job.datePublished) : 'Not published'}</p>
                                </div>
                                <div>
                                    <p className="text-gray-500">Published By</p>
                                    <p className="font-medium text-gray-900">{job.publishedByName || 'N/A'}</p>
                                </div>
                                <div>
                                    <p className="text-gray-500">Created By</p>
                                    <p className="font-medium text-gray-900">{job.createdByName || 'N/A'}</p>
                                </div>
                                <div>
                                    <p className="text-gray-500">Created On</p>
                                    <p className="font-medium text-gray-900">{job.createdAt ? formatDate(job.createdAt) : 'N/A'}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Modal */}
            <DeleteModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Job"
                subtitle="Are you sure you want to delete this job?"
                warningText="This action cannot be undone."
                isLoading={isDeleting}
                itemType="Job"
            />

            {/* Publish/Unpublish Modals */}
            {job.status === 'DRAFT' ? (
                <PublishModal
                    isOpen={statusModalOpen}
                    onClose={() => setStatusModalOpen(false)}
                    onConfirm={confirmStatusUpdate}
                    title="Publish Job"
                    subtitle="Are you sure you want to publish this job?"
                    itemType="Job"
                    isLoading={isUpdatingStatus}
                />
            ) : (
                <UnpublishModal
                    isOpen={statusModalOpen}
                    onClose={() => setStatusModalOpen(false)}
                    onConfirm={confirmStatusUpdate}
                    itemType="Job"
                    isLoading={isUpdatingStatus}
                />
            )}
        </>
    );
};

export default JobDetailPage;
